import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import FormButton from "../../Components/Buttons/FormButton";
import FormSelect from "../../Components/FormTag/FormSelect";
import FormToggle from "../../Components/FormTag/FormToggle";
import InputFiled from "../../Components/FormTag/InputFiled";
import MaskedInputField from "../../Components/FormTag/MaskedInputField";
import Loader from "../../Components/GlobalLoader/Loader";
import { DOLLAR_MASK } from "../../Shared/Helper/Masks";
import { REQ_ERR_MSG } from "../../Shared/Helper/ValidationMessage";
import { FormValidation } from "../../Utility/FormValidations";
import { formatInputData, transformLookupToSelectOptions } from "../../Utility/Utility";
import {
  AccountBasicInfoProps,
  BasicInfo
} from "./model/CompanyAccount.type";

export default function AccountBasicInfo(props: AccountBasicInfoProps) {
  const { setBasicInfo, basicInfoLookUpData, basicInfo } = props;
  const { id } = useParams();
  const navigator = useNavigate();
  const { handleNext } = props;
  const [loading, setLoading] = useState(true);
  const [lookupOptions, setLookupOptions] = useState<{
    accountTypeOptions: { label: string; value: string }[];
    accountSourceOptions: { label: string; value: string }[];
    environmentTypeOptions: { label: string; value: string }[];
  }>({
    accountTypeOptions: [],
    accountSourceOptions: [],
    environmentTypeOptions: [],
  });

  useEffect(() => {
    if (basicInfoLookUpData) {
      const accountTypeOptions = transformLookupToSelectOptions(
        basicInfoLookUpData?.accountType,
        { labelField: "description", valueField: "code" }
      );

      const accountSourceOptions = transformLookupToSelectOptions(
        basicInfoLookUpData?.accountSource,
        { labelField: "description", valueField: "code" }
      );

      const environmentTypeOptions = transformLookupToSelectOptions(
        basicInfoLookUpData?.environmentType,
        { labelField: "description", valueField: "code" }
      );

      setLookupOptions({
        accountTypeOptions,
        accountSourceOptions,
        environmentTypeOptions,
      });
    }
  }, [basicInfoLookUpData]);

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm<BasicInfo>({
    defaultValues: basicInfo,
    mode: "onChange",
  });
  
  useEffect(() => {
    if (lookupOptions.accountTypeOptions.length > 0) {
      setLoading(false);
    }
  }, [basicInfo, basicInfoLookUpData, lookupOptions]);

  useEffect(() => {
    reset(basicInfo);
  }, [basicInfo, reset]);

  const nextData: SubmitHandler<BasicInfo> = (data) => {
    if (setBasicInfo) {
     
    
      setBasicInfo({
        ...data,
        type: formatInputData(data.type),
        source: formatInputData(data.source),
        environment_type: formatInputData(data.environment_type),
      });
    }
    handleNext();
  };

  const handelCancelBtn = () => {
    reset({
      name: "",
      code: "",
      type: "",
      source: "",
      subscription_amount: "",
      subscription_users: "",
      subscription_clients: "",
      subscription_warehouses: "",
      is_active: false,
      environment_type: "",
    });
    navigator("/account/company-account/grid-view");
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <div className="my-2 bg-white rounded p-3">
        <form>
          <div className="d-flex px-2 gap-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Account Title *</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={1}
                  autoFocus={true}
                  maxLength={50}
                  type="text"
                  register={register}
                  required={true}
                  name="name"
                  errors={errors.name}
                  className={`form-control`}
                  placeholder="Enter Title"
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Account ID *</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={2}
                  type="text"
                  register={register}
                  errors={errors.code}
                  className={`form-control ${id ? "disabled" : null} `}
                  required={true}
                  name="code"
                  placeholder="Enter Account ID"
                  disable={id ? true : false}
                />
              </div>
            </div>
            <div className="col-3 d-flex px-2 justify-content-center">
                  <div className="ms-4 mt-1">
                    <label className="fw-medium">Active</label>
                  </div>
                  <div className="form-check form-switch sidebar-toggler">
                    <div className="mx-2 mt-1">
                      <FormToggle
                        register={register}
                        name="is_active"
                        tabIndex={9}
                      />
                    </div>
                  </div>
            </div>
          </div>
          <div className="d-flex px-2 gap-3 mt-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Account Type *</label>
              <div className="mt-1">
                <FormSelect
                  tabIndex={3}
                  {...register("type", {
                    required: {
                      value: true,
                      message: REQ_ERR_MSG,
                    },
                  })}
                  name="type"
                  control={control}
                  optionList={lookupOptions.accountTypeOptions}
                  className="form-control p-0"
                  placeholder="Select"
                  isSearchable={true}
                />
                {errors?.type && (
                  <div>
                    <span className="text-danger">
                      {errors?.type?.message?.toString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Account Source *</label>
              <div className="mt-1">
                <FormSelect
                  tabIndex={5}
                  name="source"
                  control={control}
                  required={true}
                  optionList={lookupOptions.accountSourceOptions}
                  className="form-control p-0"
                  placeholder="Select"
                  isSearchable={true}
                />
               
              </div>
            </div>
          </div>
          <div className="d-flex px-2 gap-3 mt-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Environment Type *</label>
              <div className="mt-1">
                <FormSelect
                  tabIndex={4}
                  {...register("environment_type", {
                    required: {
                      value: true,
                      message: REQ_ERR_MSG,
                    },
                  })}
                  name="environment_type"
                  control={control}
                  optionList={lookupOptions.environmentTypeOptions}
                  className="form-control p-0"
                  placeholder="Select"
                  isSearchable={true}
                />
                {errors?.environment_type && (
                  <div>
                    <span className="text-danger">
                      {errors.environment_type?.message?.toString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Subscription Amount</label>
              <div className="mt-1">
                <MaskedInputField
                  tabIndex={6}
                  name="subscription_amount"
                  mask={DOLLAR_MASK}
                  lazy={true}
                  eager={true}
                  control={control}
                  className={`form-control `}
                  blocks={{
                    num: {
                      mask: Number,
                      thousandsSeparator: ",",
                      radix: ".",
                      scale: 2,
                      mapToRadix: ["."],
                    },
                  }}
                  defaultValue={basicInfo?.subscription_amount}
                  placeholder="Enter Subscription Amount"
                />
              </div>
            </div>
          </div>
          <div className="d-flex px-2 gap-3 mt-3">
            <div className="col-4 px-2">
              <label className="fw-medium">No. of User(s)</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={7}
                  type="text"
                  register={register}
                  name="subscription_users"
                  errors={errors.subscription_users}
                  className="form-control"
                  placeholder="Enter No. of User(s)"
                  regex={FormValidation.numeric}
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">No. of Client(s)</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={8}
                  type="text"
                  register={register}
                  name="subscription_clients"
                  errors={errors.subscription_clients}
                  className="form-control"
                  placeholder="Enter No. of Client(s)"
                  regex={FormValidation.numeric}
                />
              </div>
            </div>
          </div>
          <div className="d-flex px-2 gap-3 mt-3">
            <div className="col-4 px-2">
              <label className="fw-medium">No. of Warehouse(s)</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={9}
                  type="text"
                  register={register}
                  name="subscription_warehouses"
                  errors={errors.subscription_warehouses}
                  className="form-control"
                  placeholder="Enter No. of Warehouse(s)"
                  regex={FormValidation.numeric}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
      <div className="my-2 bg-white rounded py-3">
        <div className="d-flex flex-row-reverse">
          <FormButton
            tabIndex={10}
            className="sph-btn me-4"
            handleClick={handleSubmit(nextData)}
          >
            Next
          </FormButton>
          <FormButton
            tabIndex={11}
            className="sph-btn sph-shift me-4"
            handleClick={handelCancelBtn}
          >
            Cancel
          </FormButton>
        </div>
      </div>
    </>
  );
}
